import type { TFunction } from "i18next";
import {
  ContractPriority,
  ContractRisk,
  ContractStateDto,
  type ContractDataFieldSectionDto,
  type ContractReportDto,
  type CustomContractDataFieldDto,
} from "../../../autogen/bff-api";
import {
  isCustomAmountField,
  isCustomBooleanField,
  isCustomDateField,
  isCustomEmailField,
  isCustomEnumField,
  isCustomMultiEnumField,
  isCustomNumberField,
  isCustomPercentageField,
  isCustomPersonField,
  isCustomPhoneField,
  isCustomRichTextField,
  isCustomTagField,
  isCustomTextField,
  isCustomUrlField,
} from "../../../common/types";
import { osloDate, type ExportCell } from "../../../common/download/listExport";
import { tiptapToPlainText } from "../../../common/input/TipTap/tiptapToPlainText";
import { contractLevelLabel } from "../../../common/useTranslationTools";

export interface ExportContext {
  t: TFunction;
  selectedOrgId: string;
}

export const NOT_EXPORTED_COLUMNS: ReadonlySet<string> = new Set(["actions"]);

export const contractListFileName = (now: Date): string => `Kontrakter-${now.toLocaleDateString("sv-SE")}`;

export const exportCell = (key: string, contract: ContractReportDto, ctx: ExportContext): ExportCell => {
  const { t } = ctx;
  switch (key) {
    case "title":
      return { text: contract.title, contractId: contract.contractId };
    case "counterparty":
      return text(contract.counterpartyName);
    case "type":
      return text(contract.templateName);
    case "frameworkRole":
      return text(t(contract.frameworkRole));
    case "projectDetails":
      return joined(contract.projectDetails?.map((p) => p.projectName));
    case "internalParties":
      return joined(contract.internalParties?.map((p) => p.organizationName));
    case "state":
      return text(contract.state === ContractStateDto.Published ? t("Published") : t("Draft"));
    case "status":
      return text(contract.status ? t(contract.status) : null);
    case "startDate":
      return date(contract.startDate);
    case "endDate":
      return date(contract.endDate);
    case "hasNoEndDate":
      return text(contract.endDate ? t("No") : t("Yes"));
    case "responsible":
      return text(contract.internalOwnerName);
    case "estimatedValue":
      return number(contract.estimatedValue);
    case "totalPrice":
      return number(contract.totalPrice);
    case "priority":
      return text(contract.priority ? contractLevelLabel(contract.priority as ContractPriority, t) : null);
    case "risk":
      return text(contract.risk ? contractLevelLabel(contract.risk as ContractRisk, t) : null);
    case "referenceNumber":
      return text(contract.referenceNumber);
    case "createdAt":
      return date(contract.createdAt);
    case "modifiedAt":
      return date(contract.modifiedAt);
    case "publishedAt":
      return date(contract.publishedAt);
    case "version":
      return number(contract.version);
    case "description":
      return text(contract.description);
    case "timezone":
      return text(contract.timezone);
    case "currency":
      return text(contract.currency);
    case "createdBy":
      return text(contract.createdByName);
    case "renewalDate":
      return date(contract.renewalDate);
    case "originSourcingEvent":
      return text(contract.originSourcingEventName);
    case "publishedBy":
      return text(contract.publishedByName);
    case "noticePeriod":
      return number(contract.noticePeriod);
    case "documentCount":
      return number(contract.documentIds?.length ?? 0);
    case "signedDocumentCount":
      return number(contract.signedDocumentIds?.length ?? 0);
    case "terminationDate":
      return date(contract.terminationDate);
    case "terminatedBy":
      return text(contract.terminatedById ? contract.terminatedByName : null);
    case "terminationReason":
      return text(contract.terminationReason);
    case "counterpartyContactFullName":
      return text(contract.counterpartyContactFullName);
    case "counterpartyContactEmail":
      return text(contract.counterpartyContactEmail);
    case "counterpartyContactPhone":
      return text(contract.counterpartyContactPhoneNumber);
    case "externalLink":
      return text(contract.externalLink);
    case "linkedContracts":
      return joined(contract.linkedContracts?.map((c) => c.title ?? ""));
    case "paymentTerms":
      return number(contract.paymentTermsInDays);
    case "pricePerMonth":
      return number(contract.pricePerMonth);
    case "pricePerYear":
      return number(contract.pricePerYear);
    case "annualValue":
      return number(contract.annualValue);
    case "purchasingPolicy":
      return text(contract.purchasingPolicy);
    case "supplierInfo":
      return text(
        contract.supplierInfo
          ? [
              `${t("Is climate committed")}: ${contract.supplierInfo.isClimateCommitted ? t("Yes") : t("No")}`,
              `${t("Has signed code of conduct")}: ${contract.supplierInfo.hasSignedCodeOfConduct ? t("Yes") : t("No")}`,
              `${t("Has customer contact")}: ${contract.supplierInfo.hasCustomerContact ? t("Yes") : t("No")}`,
            ].join(", ")
          : null,
      );
    case "totalLiability":
      return number(contract.totalLiability);
    case "warrantyExpiration":
      return date(contract.warrantyExpirationDate);
    case "supplierCategory":
      return text(contract.supplierCategory);
    case "options":
      return joined(contract.options?.map((o) => o.name ?? ""));
    case "owner":
      return text(contract.owningOrganizationId !== ctx.selectedOrgId ? contract.owningOrganizationName : t("Your org"));
    default:
      return customFieldCell(customFieldOf(contract, key), t);
  }
};

const customFieldOf = (contract: ContractReportDto, fieldId: string): CustomContractDataFieldDto | null => {
  for (const section of Object.values(contract.customFields ?? {})) {
    const field = (section as ContractDataFieldSectionDto).fields.find((f) => f.id === fieldId);
    if (field) return field;
  }
  return null;
};

const customFieldCell = (field: CustomContractDataFieldDto | null, t: TFunction): ExportCell => {
  if (!field) return null;
  if (isCustomAmountField(field)) return number(field.amount);
  if (isCustomNumberField(field)) return number(field.value);
  if (isCustomPercentageField(field))
    return field.value === null || field.value === undefined ? null : { percent: field.value };
  if (isCustomDateField(field)) return date(field.date);
  if (isCustomEmailField(field)) return text(field.email);
  if (isCustomBooleanField(field)) return text(field.isChecked ? t("Yes") : t("No"));
  if (isCustomEnumField(field)) return text(field.value);
  if (isCustomMultiEnumField(field)) return joined(field.values);
  if (isCustomPersonField(field))
    return text(field.person ? `${field.person.firstName ?? ""} ${field.person.lastName ?? ""}`.trim() : null);
  if (isCustomPhoneField(field)) return text(field.number);
  if (isCustomRichTextField(field))
    return text(tiptapToPlainText(field.text));
  if (isCustomTextField(field)) return text(field.text);
  if (isCustomTagField(field)) return text(field.tag);
  if (isCustomUrlField(field)) return text(field.url);
  return null;
};

const text = (value: string | null | undefined): ExportCell => (value ? { text: value } : null);

const number = (value: number | null | undefined): ExportCell =>
  value === null || value === undefined ? null : { number: value };

const date = (value: string | null | undefined): ExportCell => (value ? { date: osloDate(value) } : null);

const joined = (values: (string | null | undefined)[] | null | undefined): ExportCell =>
  text(values?.filter((v): v is string => !!v).join(", "));


export interface ContractListQuery {
  q: Record<string, unknown>;
  sortBy?: string;
  sortDirection?: string;
  sortByCustomField?: string;
}

